/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { ICoreContext, msdyn365Commerce } from '@msdyn365-commerce/core-internal';
import {
    CommerceProperty,
    ProductDeliveryOptions,
    ProductDimensionValue,
    ProductPrice,
    ReleasedProductType,
    SimpleProduct /*AsyncResult*/,
    CartLine
} from '@msdyn365-commerce/retail-proxy';
import { BulkPurchase } from '@msdyn365-commerce-modules/bulk-purchase';
import {
    ArrayExtensions,
    DeliveryMode,
    FinitePromiseQueue,
    FinitePromiseQueueError,
    getDeliveryOptionsForSelectedVariant,
    GetDeliveryOptionsForSelectedVariantInput,
    getDimensionsForSelectedVariant,
    GetDimensionsForSelectedVariantInput,
    getInventoryLevelCodeFromDimensionValue,
    getPriceForSelectedVariant,
    getProductAvailabilitiesForSelectedVariant,
    getSelectedVariant,
    IDimensionsApp,
    IDimensionValueForSelectedVariant,
    InventoryLevelValues,
    IProductInventoryInformation,
    IPromiseQueue,
    PriceForSelectedVariantInput,
    ProductAvailabilitiesForSelectedVariantInput,
    SelectedVariantInput,
    setDimensionValuesToQuery
} from '@msdyn365-commerce-modules/retail-actions';
import { Button, getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';
import { Overlay } from 'react-portal-overlay';
import { GetOrderSummaryStateValuesInput } from '../../actions/eShopOrderSummary/eshopOrderSummary';
import {
    getBuyboxAddToCart,
    getBuyBoxInventoryLabel,
    getBuyboxKeyInPrice,
    getBuyboxProductAddToOrderTemplate,
    getBuyboxProductAddToWishlist,
    getBuyboxProductDescription,
    getBuyboxProductPrice,
    getBuyboxProductQuantity,
    getBuyboxProductRating,
    getBuyboxProductTitle,
    getBuyboxShopSimilarLook,
    getQuantityLimitsMessages,
    IBuyboxAddToCartViewProps,
    IBuyboxAddToOrderTemplateViewProps,
    IBuyboxAddToWishlistViewProps,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxShopSimilarLookViewProps,
    RetailDefaultOrderQuantityLimitsFeatureName
} from './components/eshop-buybox-samll-components';

import { getBuyboxProductConfigure } from './components/eshop-buybox-product-configure';
import { IBuyboxProductConfigureViewProps } from './components/buyboxInterface';

import { IBuyboxCommonData, IBuyboxExtentedProps, IErrorState } from '@msdyn365-commerce-modules/buybox';
import { IEshopBuyboxData } from './eshop-buybox.data';
import { IEshopBuyboxProps, IEshopBuyboxResources } from './eshop-buybox.props.autogenerated';
import { getBuyboxFindInStore, IBuyboxFindInStoreViewProps } from './components/eshop-buybox-find-in-store';

import { getCartState } from '@msdyn365-commerce/global-state';
import { Address, DeliverySpecification } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { updateDeliverySpecificationAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';

export interface IBuyboxCallbacks {
    updateQuantity(newQuantity: number): boolean;
    updateRemarks(remarks: string): boolean;
    clearRemarks(): void;
    updateKeyInPrice(keyInPrice: number): void;
    updateErrorState(newErrorState: IErrorState): void;
    updateSelectedProduct(
        selectedProduct: Promise<SimpleProduct | null>,
        newInventory: IProductInventoryInformation | undefined,
        newPrice: ProductPrice | undefined,
        newDeliveryOptions: ProductDeliveryOptions | undefined
    ): void;
    getDropdownName(dimensionType: number, resources: IEshopBuyboxResources): string;
    dimensionSelectedAsync(selectedDimensionId: number, selectedDimensionValueId: string): Promise<void>;
    changeModalOpen(isModalOpen: boolean): void;
    changeUpdatingDimension(isUpdatingDimension: boolean): void;
    changeUpdatingDeliveryOptions?(isUpdatingDeliveryOptions: boolean): void;
    changeCollapseOpen(isCollapseOpen: boolean): void;
    updateDeliverySpecs(props: IEshopBuyboxProps<IEshopBuyboxData>): void;
}

export interface IBuyboxRemarksViewProps {
    ContainerProps: INodeProps;
    LabelContainerProps: INodeProps;
    ErrorContainerProps: INodeProps;

    heading: React.ReactNode;
    errors?: React.ReactNode;

    input: React.ReactNode;
}

export interface IBuyboxViewProps extends IEshopBuyboxProps<IEshopBuyboxData> {
    state: IBuyboxState;
    ModuleProps: IModuleProps;
    ProductInfoContainerProps: INodeProps;
    MediaGalleryContainerProps: INodeProps;
    callbacks: IBuyboxCallbacks;
    mediaGallery?: React.ReactNode;
    title?: React.ReactNode;
    description?: React.ReactNode;
    rating?: React.ReactNode;
    price?: React.ReactNode;
    bulkPurchaseLink?: React.ReactNode;
    addToOrderTemplate?: IBuyboxAddToOrderTemplateViewProps;
    addToWishlist?: IBuyboxAddToWishlistViewProps;
    max: number | undefined;
    addToCart: IBuyboxAddToCartViewProps;
    findInStore?: IBuyboxFindInStoreViewProps;
    quantity?: IBuyboxProductQuantityViewProps;
    remarks?: IBuyboxRemarksViewProps;
    configure?: IBuyboxProductConfigureViewProps;
    inventoryLabel?: React.ReactNode;
    shopSimilarLook?: IBuyboxShopSimilarLookViewProps;
    shopSimilarDescription?: IBuyboxShopSimilarLookViewProps;
    quantityLimitsMessages: React.ReactNode;
    telemetryContent?: ITelemetryContent;
    keyInPrice?: IBuyboxKeyInPriceViewProps;
    unitOfMeasure?: React.ReactNode;
    eshopOrderSummary?: React.ReactNode;
    orderSummaryContainerProps: INodeProps;
    moreProductDetailsContainerProps: INodeProps;
    moreProductDetails?: React.ReactNode;
}

export interface IBuyboxState {
    quantity: number;
    remarks: string;
    min: number | undefined;
    max: number | undefined;
    errorState: IErrorState;
    selectedProduct?: Promise<SimpleProduct | null>;
    productAvailableQuantity?: IProductInventoryInformation;
    productPrice?: ProductPrice;
    productDeliveryOptions?: ProductDeliveryOptions;
    modalOpen?: boolean;
    isUpdatingDimension?: boolean;
    isUpdatingDeliveryOptions?: boolean;
    isServiceItem?: boolean;
    isPriceKeyedIn?: boolean;
    keyInPriceAmount?: number;
    isCustomPriceSelected?: boolean;
    openOverlay: boolean;
    isCollapseOpen?: boolean;
    cartLineId?: string;
}

/**
 * Buybox Module.
 */
class Buybox extends React.PureComponent<IEshopBuyboxProps<IEshopBuyboxData>, IBuyboxState> {
    /**
     * A queue of tasks of processing the changes in the dimensions.
     * Limit to two processes:
     * 1 - for the current process, which is under execution at the moment.
     * 2 - next process, which will process the latest version of data.
     * @remark Enqueueing new promises will discard the previous ones (except the one which is under processing).
     */
    private readonly dimensionUpdateQueue: IPromiseQueue<void> = new FinitePromiseQueue<void>(2);

    private dimensions: { [id: number]: string } = {};

    private readonly buyboxCallbacks: IBuyboxCallbacks = {
        updateQuantity: (newQuantity: number): boolean => {
            const errorState = { ...this.state.errorState };
            errorState.quantityError = undefined;
            errorState.otherError = undefined;

            this.setState({ quantity: newQuantity, errorState });
            return true;
        },
        updateRemarks: (remarks: string) => {
            const errorState = { ...this.state.errorState };
            errorState.quantityError = undefined;
            errorState.otherError = undefined;
            this.setState({ remarks: remarks, errorState });
            return true;
        },
        clearRemarks: () => {
            const errorState = { ...this.state.errorState };
            errorState.quantityError = undefined;
            errorState.otherError = undefined;
            this.setState({ remarks: '', errorState });
        },
        updateErrorState: (newErrorState: IErrorState): void => {
            this.setState({ errorState: newErrorState });
        },
        updateSelectedProduct: (
            newSelectedProduct: Promise<SimpleProduct | null>,
            newInventory: IProductInventoryInformation | undefined,
            newPrice: ProductPrice | undefined,
            newDeliveryOptions: ProductDeliveryOptions | undefined
        ): void => {
            this.setState({
                selectedProduct: newSelectedProduct,
                productAvailableQuantity: newInventory,
                productDeliveryOptions: newDeliveryOptions
            });
            this._updatePrice(newPrice);
        },
        dimensionSelectedAsync: async (selectedDimensionId: number, selectedDimensionValueId: string): Promise<void> => {
            this.dimensions[`${selectedDimensionId}`] = selectedDimensionValueId;
            return this.dimensionUpdateQueue
                .enqueue(async () => {
                    return this._updateDimensions();
                })
                .catch((error: any) => {
                    // Ignore discarded processes.
                    if (error !== FinitePromiseQueueError.ProcessWasDiscardedFromTheQueue) {
                        throw error;
                    }
                });
        },
        getDropdownName: (dimensionType: number, resources: IEshopBuyboxResources): string => {
            return this._getDropdownName(dimensionType, resources);
        },
        changeModalOpen: (isModalOpen: boolean): void => {
            this.setState({ modalOpen: isModalOpen });
            this.forceUpdate();
        },
        changeUpdatingDimension: (isUpdatingDimension: boolean): void => {
            this.setState({ isUpdatingDimension });
        },

        /**
         * Update isUpdatingDeliveryOptions state.
         *
         * @param isUpdatingDeliveryOptions - The status of updating delivery options.
         */
        changeUpdatingDeliveryOptions: (isUpdatingDeliveryOptions: boolean): void => {
            this.setState({ isUpdatingDeliveryOptions });
        },

        updateKeyInPrice: (customPrice: number): void => {
            // Remove custom amount error when updating the custom price
            const errorState = { ...this.state.errorState };
            errorState.customAmountError = undefined;

            this.setState({
                isPriceKeyedIn: true,
                keyInPriceAmount: customPrice,
                errorState
            });
            this._updatePrice(this.state.productPrice, customPrice);
        },
        changeCollapseOpen: (isCollapseOpen: boolean): void => {
            this.setState({ isCollapseOpen: isCollapseOpen });
        },
        updateDeliverySpecs: (props: IEshopBuyboxProps<IEshopBuyboxData>): void => {
            void this.updateDeliverySpecsInCart(props);
        }
    };

    private readonly telemetryContent: ITelemetryContent;

    public constructor(props: IEshopBuyboxProps<IEshopBuyboxData>, state: IBuyboxState) {
        super(props);
        this.state = {
            errorState: {
                configureErrors: {}
            },
            quantity: 1,
            remarks: '',
            min: undefined,
            max: undefined,
            selectedProduct: undefined,
            productPrice: undefined,
            productDeliveryOptions: undefined,

            modalOpen: false,
            isUpdatingDimension: false,
            isUpdatingDeliveryOptions: false,
            openOverlay: props.context.request.user.isAuthenticated ? false : true,
            isCollapseOpen: true
        };
        this.telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
    }

    public async componentDidMount(): Promise<void> {
        const {
            data: {
                product: { result: product },
                productPrice
            }
        } = this.props;

        void productPrice.then(async result => {
            this._updatePrice(result);
        });

        if (product) {
            // Check if the product is service or not by product type
            if (product.ItemTypeValue === ReleasedProductType.Service) {
                this.setState({ isServiceItem: true });
            }
            await this._updateQuantitiesInState(product);
        }

        const parameters = window.location.search;
        const cartlineId = new URLSearchParams(parameters).get('cartlineId');
        this.setState({ cartLineId: cartlineId! });

        if (cartlineId) {
            const cartState = await getCartState(this.props.context.actionContext);
            const cart = cartState.cart;
            const cartLines: CartLine[] = cart.CartLines!;
            const cartLineItem = cartLines.find(cartline => cartline.LineId === cartlineId);
            if (cartLineItem) {
                this.buyboxCallbacks.updateQuantity(cartLineItem.Quantity!);
            }
        }
    }

    public render(): JSX.Element | null {
        const {
            slots: { mediaGallery, orderSummary, moreAboutProduct },
            data: {
                product: { result: product }
            },
            config: { className = '' },
            context: {
                request: { user }
            }
        } = this.props;

        const { max } = this.state;

        if (!product) {
            this.props.context.telemetry.error('Product content is empty, module wont render');
            return null;
        }
        const props = this.props as IBuyboxExtentedProps<IBuyboxCommonData>;
        const defaultMinimumKeyInPrice = 10;
        const defaultMaximumKeyInPrice = 100;

        const context = this.props.context as ICoreContext<IDimensionsApp>;
        const inventoryLevel = context.app.config.inventoryLevel;
        const dimensionValuesWithInventory = ArrayExtensions.flatten(
            ArrayExtensions.validValues(this.props.data.productDimensions.result?.map(value => value.dimensionValuesWithInventory))
        );
        const hasAvailableProducts =
            !ArrayExtensions.hasElements(dimensionValuesWithInventory) ||
            dimensionValuesWithInventory.some(value => {
                const inventoryLevelCode = getInventoryLevelCodeFromDimensionValue(value, inventoryLevel);
                return inventoryLevelCode !== InventoryLevelValues.outOfStock;
            });
        const orderSummaryClassName = this.state.modalOpen
            ? 'ms-buybox__order-summary-display col-sm-6'
            : 'ms-buybox__order-summary-notdisplay col-sm-6';
        this.props.context.actionContext.update(new GetOrderSummaryStateValuesInput(), { modelOpenValue: this.state.modalOpen });
        const viewProps: IBuyboxViewProps = {
            ...(this.props as IEshopBuyboxProps<IEshopBuyboxData>),
            state: this.state,
            mediaGallery: mediaGallery && mediaGallery.length > 0 ? mediaGallery[0] : undefined,
            ModuleProps: {
                moduleProps: this.props,
                className: classnames('eshop-buybox ms-buybox row', className)
            },
            ProductInfoContainerProps: {
                className: 'ms-buybox__content col-sm-6 px-4 pl-md-8'
            },
            MediaGalleryContainerProps: {
                className: 'ms-buybox__media-gallery col-sm-6'
            },
            orderSummaryContainerProps: {
                className: orderSummaryClassName
            },
            moreProductDetailsContainerProps: {
                className: 'ms-buybox__more-product-details-display'
            },

            telemetryContent: this.telemetryContent,
            callbacks: this.buyboxCallbacks,
            title: getBuyboxProductTitle(this.props),
            description: getBuyboxProductDescription(this.props),
            bulkPurchaseLink: this._renderBuyboxBulkPurchaseLink(),
            //unitOfMeasure: getBuyboxProductUnitOfMeasure(props),
            configure: getBuyboxProductConfigure(props, this.state, this.buyboxCallbacks),
            findInStore: getBuyboxFindInStore(this.props, this.state, this.buyboxCallbacks),
            price: getBuyboxProductPrice(this.props, this.state),
            addToCart: getBuyboxAddToCart(
                this.props,
                this.state,
                this.buyboxCallbacks,
                defaultMinimumKeyInPrice,
                defaultMaximumKeyInPrice,
                undefined,
                hasAvailableProducts
            ),
            addToOrderTemplate: getBuyboxProductAddToOrderTemplate(this.props, this.state, this.buyboxCallbacks),
            addToWishlist: getBuyboxProductAddToWishlist(this.props, this.state, this.buyboxCallbacks),
            rating: !props.context.app.config.hideRating && getBuyboxProductRating(this.props),
            quantity: product.IsGiftCard ? undefined : getBuyboxProductQuantity(this.props, this.state, this.buyboxCallbacks),
            inventoryLabel: getBuyBoxInventoryLabel(this.props),
            shopSimilarLook:
                this.props.config.enableShopSimilarLooks && !product.IsGiftCard
                    ? getBuyboxShopSimilarLook(this.props, this.state, this.buyboxCallbacks)
                    : undefined,
            //shopSimilarDescription: this.props.config.enableShopSimilarDescription && !product.IsGiftCard ? getBuyboxShopSimilarButton(props, ShopSimiliarButtonType.Description) : undefined,
            keyInPrice:
                this.props.config.enableKeyInPrice && this.state.isCustomPriceSelected
                    ? getBuyboxKeyInPrice(this.props, this.state, this.buyboxCallbacks)
                    : undefined,
            quantityLimitsMessages: getQuantityLimitsMessages(this.props, this.state),
            max,
            eshopOrderSummary: orderSummary && orderSummary.length > 0 ? orderSummary[0] : undefined,
            moreProductDetails: moreAboutProduct && moreAboutProduct.length > 0 ? moreAboutProduct[0] : undefined,
            remarks: this._getBuyboxRemarks(this.props, this.state, this.buyboxCallbacks)
        };
        const isZH = this.props.context.request.locale.split('-')[0] === 'zh';
        return (
            <>
                {this.props.renderView(viewProps) as React.ReactElement}
                <Overlay open={this.state.openOverlay} closeOnClick>
                    <div className='overlay-content' onClick={() => this.setState({ openOverlay: false })}>
                        <div
                            className='popup-card'
                            onClick={event => {
                                if (event.target !== event.currentTarget) {
                                    event.stopPropagation();
                                }
                            }}
                        >
                            <div className='popup-header'>{isZH ? '事先请登入' : 'Please Sign in First'}</div>
                            <div className='popup-subtext'>
                                {isZH
                                    ? '请先登入户口以避免失去在此页注入的数据'
                                    : 'If you have an account with us, please sign in first to avoid losing any information entered on this page'}
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-evenly',
                                    alignItems: 'center',
                                    width: '100%'
                                }}
                            >
                                <a href={user && user.signInUrl} className='signin-popup'>
                                    {isZH ? '登入户口' : 'Go to Sign In Page'}
                                </a>
                                <div onClick={() => this.setState({ openOverlay: false })} className='dismiss-popup'>
                                    {isZH ? '也许稍后' : 'Maybe Later'}
                                </div>
                            </div>
                        </div>
                    </div>
                </Overlay>
            </>
        );
    }

    private readonly _getBuyboxRemarks = (
        props: IEshopBuyboxProps<IEshopBuyboxData>,
        state: IBuyboxState,
        callbacks: IBuyboxCallbacks
    ): IBuyboxRemarksViewProps => {
        const remarksError = '';
        const isZH = this.props.context.request.locale.split('-')[0] === 'zh';
        const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = event.target.value;
            if (callbacks.updateRemarks) {
                return callbacks.updateRemarks(newValue);
            }
            return true;
        };
        return {
            heading: <div className='eshop-buybox__product-quantity-label-heading'>Remarks</div>,
            input: (
                <div>
                    <input
                        className='remarks-input'
                        placeholder={isZH ? '在此留言…' : 'Leave a message…'}
                        type='text'
                        maxLength={10}
                        onChange={e => onChange(e)}
                        value={this.state.remarks}
                    />
                </div>
            ),
            ContainerProps: {
                className: 'eshop-buybox ms-buybox__quantity row'
            },
            ErrorContainerProps: {
                tag: 'label',
                className: 'eshop-buybox__product-quantity-label',
                htmlFor: 'eshop-buybox__product-quantity-input'
            },
            LabelContainerProps: {
                tag: 'label',
                className: 'eshop-buybox__product-quantity-label col-md-2 col-2 col-sm-2',
                htmlFor: 'eshop-buybox__product-quantity-input'
            },
            errors: remarksError && (
                <span className='msc-alert msc-alert-noborder msc-alert-danger'>
                    <span className='msi-exclamation-triangle' aria-hidden='true' />
                    <span>{remarksError}</span>
                </span>
            )
        };
    };

    /**
     * Handles anchor click event and scrolls to the given bulk purchase module.
     * @param bulkPurchaseModule - Bulk purchase module.
     * @returns Handler.
     */
    private readonly _onBulkPurchaseLinkClick = (bulkPurchaseModule: Element) => () => {
        const boundingRect = bulkPurchaseModule.getBoundingClientRect();
        window.scrollTo(boundingRect.left, boundingRect.top);
    };

    private _renderBuyboxBulkPurchaseLink(): React.ReactNode {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Temporary switch for the feature which is under development.
        if (!this.props.context.app.config.isBulkPurchaseEnabled) {
            return null;
        }

        if (!msdyn365Commerce.isBrowser) {
            return null;
        }

        const bulkPurchaseModules = Array.from(document.getElementsByClassName(BulkPurchase.className));

        if (!ArrayExtensions.hasElements(bulkPurchaseModules)) {
            return null;
        }

        return (
            <Button
                className='ms-buybox__bulk-purchase-anchor'
                title={this.props.resources.bulkPurchaseLinkText}
                onClick={this._onBulkPurchaseLinkClick(bulkPurchaseModules[0])}
            >
                {this.props.resources.bulkPurchaseLinkText}
            </Button>
        );
    }

    private _updatePrice(newPrice: ProductPrice | undefined, customPrice: number | undefined = this.state.keyInPriceAmount): void {
        if (this.state.isCustomPriceSelected && newPrice) {
            newPrice.CustomerContextualPrice = customPrice;
        }
        this.setState({ productPrice: newPrice });
    }

    private _getProductVariant(dimensionsToUpdate: { [id: number]: string }) {
        const {
            data: {
                product: { result: product }
            },
            context: {
                actionContext,
                request: {
                    apiSettings: { channelId }
                }
            }
        } = this.props;

        const productDimensions = this.props.data.productDimensions.result!;

        // Step 3, Build the actually selected dimensions, prioritizing the information in state
        // over the information in data
        const mappedDimensions = productDimensions
            .map(dimension => {
                const dimensions = (dimension.dimensionValuesWithInventory ??
                    dimension.DimensionValues ??
                    []) as IDimensionValueForSelectedVariant[];
                return {
                    DimensionTypeValue: dimension.DimensionTypeValue,
                    DimensionValue:
                        dimensions.find(
                            value => value.DimensionValue?.RecordId.toString() === dimensionsToUpdate[dimension.DimensionTypeValue]
                        )?.DimensionValue ?? dimension.DimensionValue,
                    ExtensionProperties: dimension.ExtensionProperties
                };
            })
            .filter(dimension => dimension.DimensionValue);
        setDimensionValuesToQuery(this.props.context.actionContext.requestContext.url.requestUrl, mappedDimensions);

        // Step 4. Use these dimensions hydrate the product. Wrap this in a promise
        // so that places like add to cart can await it
        const selectedProductPromise = getSelectedVariant(
            new SelectedVariantInput(
                product!.MasterProductId ? product!.MasterProductId : product!.RecordId,
                channelId,
                undefined,
                undefined,
                this.props.context.request
            ),
            actionContext
        );
        this.setState({ selectedProduct: selectedProductPromise });

        return { productVariantPromise: selectedProductPromise, mappedDimensions };
    }

    private readonly _updateDimensions = async (): Promise<void> => {
        const product = this.props.data.product.result;

        const productDimensions = this.props.data.productDimensions.result;

        const hasProductDimensions = ArrayExtensions.hasElements(productDimensions);

        if (!product || !hasProductDimensions) {
            return;
        }

        const dimensionsToUpdate: { [id: number]: string } = { ...this.dimensions };
        this.setState({
            isUpdatingDimension: true,
            isUpdatingDeliveryOptions: true
        });

        // Step 1: Clear error state to display relevant errors
        if (this.state.errorState.otherError || this.state.errorState.quantityError) {
            const clearErrorState = { ...this.state.errorState };
            clearErrorState.otherError = undefined;
            if (this.state.errorState.errorHost === 'ADDTOCART') {
                clearErrorState.quantityError = undefined;
                clearErrorState.errorHost = undefined;
            }
            this.setState({ errorState: clearErrorState });
        }

        // Step 2: Clear any errors indicating the dimension wasn't selected
        for (const key of Object.keys(dimensionsToUpdate)) {
            if (this.state.errorState.configureErrors[`${key}`]) {
                this.setState(previousState => {
                    const errorState = previousState.errorState;
                    errorState.configureErrors[`${key}`] = undefined;
                    return { errorState };
                });
            }
        }

        const data = this._getProductVariant(dimensionsToUpdate);
        const productVariant: SimpleProduct | null = await data.productVariantPromise;

        if (!productVariant) {
            return;
        }

        const promises: Promise<void>[] = [
            this._getDimensionsForSelectedVariant(productVariant, data.mappedDimensions),
            this._hydrateInventoryInfo(productVariant),
            this._hydratePrice(productVariant),
            this._hydrateDeliveryOptions(productVariant),
            this._updateQuantitiesInState(productVariant)
        ];

        await Promise.all(promises);
    };

    private async _getDimensionsForSelectedVariant(
        productVariant: SimpleProduct,
        mappedDimensions: {
            /* eslint-disable @typescript-eslint/naming-convention -- Retail proxy values */
            DimensionTypeValue: number;
            DimensionValue: ProductDimensionValue | undefined;
            ExtensionProperties: CommerceProperty[] | undefined;
            /* eslint-enable @typescript-eslint/naming-convention -- Retail proxy values */
        }[]
    ): Promise<void> {
        await getDimensionsForSelectedVariant(
            new GetDimensionsForSelectedVariantInput(
                productVariant.MasterProductId ? productVariant.MasterProductId : productVariant.RecordId,
                this.props.context.request.apiSettings.channelId,
                mappedDimensions,
                this.props.context.request
            ),
            this.props.context.actionContext
        );
    }

    private async _hydrateInventoryInfo(productVariant: SimpleProduct): Promise<void> {
        // Step 5. Use these dimensions hydrate the inventory. Wrap this in a promise
        // so that places like add to cart can await it
        const newAvailableQuantity = await getProductAvailabilitiesForSelectedVariant(
            new ProductAvailabilitiesForSelectedVariantInput(productVariant.RecordId, this.props.context.request.apiSettings.channelId),
            this.props.context.actionContext
        );

        const newShippingQuantity = newAvailableQuantity?.find(shipping => shipping.deliveryType === DeliveryMode.shipping);
        const isCustomPriceSelected = productVariant.Dimensions?.find(
            dimension =>
                dimension.DimensionTypeValue === 4 && dimension.DimensionValue && dimension.DimensionValue.Value?.toLowerCase() === 'custom'
        );
        if (isCustomPriceSelected) {
            this.setState({ isCustomPriceSelected: true });
        } else {
            this.setState(previousState => {
                const errorState = previousState.errorState;
                errorState.customAmountError = undefined;
                return {
                    isCustomPriceSelected: false,
                    isPriceKeyedIn: false,
                    errorState
                };
            });
        }

        if (newShippingQuantity) {
            this.setState({ productAvailableQuantity: newShippingQuantity });
        } else {
            this.setState({ productAvailableQuantity: undefined });
        }
    }

    private async _hydratePrice(productVariant: SimpleProduct): Promise<void> {
        // Step 6. Use these dimensions hydrate the product price.
        const newPrice = await getPriceForSelectedVariant(
            new PriceForSelectedVariantInput(productVariant.RecordId, this.props.context.request.apiSettings.channelId),
            this.props.context.actionContext
        );

        if (newPrice) {
            this._updatePrice(newPrice);
        }
    }

    private async _hydrateDeliveryOptions(productVariant: SimpleProduct): Promise<void> {
        const retailMultiplePickupFeatureState = this.props.data.featureState.result?.find(
            featureState => featureState.Name === 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature'
        );

        // Step 7. Use these dimensions hydrate the product delivery options.
        const newDeliveryOptions = await getDeliveryOptionsForSelectedVariant(
            new GetDeliveryOptionsForSelectedVariantInput(
                productVariant.RecordId,
                this.props.context.request.apiSettings.channelId,
                undefined,
                undefined,
                retailMultiplePickupFeatureState?.IsEnabled
            ),
            this.props.context.actionContext
        );

        if (newDeliveryOptions) {
            this.setState({ productDeliveryOptions: newDeliveryOptions });
        }
    }

    private async _updateQuantitiesInState(product: SimpleProduct): Promise<void> {
        const isOrderQuantityLimitsFeatureEnabled = await this._isOrderQuantityLimitsFeatureEnabled();
        if (isOrderQuantityLimitsFeatureEnabled && product) {
            this.setState({
                min: product.Behavior?.MinimumQuantity || 1,

                // If max by feature in default order settings is not defined then use max from site settings or default max 10.
                max:
                    product.Behavior?.MaximumQuantity && product.Behavior?.MaximumQuantity > 0
                        ? product.Behavior?.MaximumQuantity
                        : this.props.context.app.config.maxQuantityForCartLineItem || 10
            });
        } else {
            this.setState({
                min: 1,
                max: this.props.context.app.config.maxQuantityForCartLineItem || 10
            });
        }
    }

    private readonly _getDropdownName = (dimensionType: number, resources: IEshopBuyboxResources): string => {
        const isGiftCard = this.props.data.product.result?.IsGiftCard;

        switch (dimensionType) {
            case 1: // ProductDimensionType.Color
                return resources.productDimensionTypeColor;
            case 2: // ProductDimensionType.Configuration
                return resources.productDimensionTypeConfiguration;
            case 3: // ProductDimensionType.Size
                return resources.productDimensionTypeSize;
            case 4: // ProductDimensionType.Style
                return isGiftCard ? resources.productDimensionTypeAmount : resources.productDimensionTypeStyle;
            default:
                return '';
        }
    };

    private async _isOrderQuantityLimitsFeatureEnabled(): Promise<boolean> {
        const defaultOrderQuantityLimitsFeatureConfig = this.props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits;
        if (defaultOrderQuantityLimitsFeatureConfig === 'none') {
            return false;
        }

        const featureStatuses = await this.props.data.featureState;
        const isFeatureEnabledInHq = featureStatuses?.find(
            featureState => featureState.Name === RetailDefaultOrderQuantityLimitsFeatureName
        )?.IsEnabled;
        if (!isFeatureEnabledInHq) {
            return false;
        }

        if (defaultOrderQuantityLimitsFeatureConfig === 'all') {
            return true;
        }
        let customerInfo;
        try {
            customerInfo = await this.props.data.customerInformation;
        } catch (error) {
            this.props.telemetry.information('error');
            this.props.telemetry.debug('Unable to receive Customer Information. May be user is not authorized');
            return false;
        }

        return (
            customerInfo &&
            ((defaultOrderQuantityLimitsFeatureConfig === 'b2b' && customerInfo.IsB2b) ||
                (defaultOrderQuantityLimitsFeatureConfig === 'b2c' && !customerInfo.IsB2b))
        );
    }

    private async updateDeliverySpecsInCart(props: IEshopBuyboxProps<IEshopBuyboxData>): Promise<void> {
        // const { data: { cart: cart } } = props;

        const cartState = await getCartState(props.context.actionContext);
        const cart = cartState.cart;

        const cartVersion = cart.Version;
        let updatedCartVersion;

        if (cartVersion) {
            try {
                const enum DeliveryPreferenceType {
                    None = 0,
                    ShipToAddress = 1,
                    PickupFromStore = 2,
                    ElectronicDelivery = 3,
                    DeliverItemsIndividually = 4
                }

                const devliveryAddress: Address = {
                    Name: 'Dummy Address',
                    FullAddress: 'Dummy Address Full Address',
                    RecordId: 1,
                    Street: 'Dummy Street',
                    StreetNumber: '10A',
                    City: 'Singapore',
                    State: '',
                    ZipCode: '000000',
                    ThreeLetterISORegionName: 'SGP',
                    TwoLetterISORegionName: 'SG',
                    Deactivate: false,
                    IsPrimary: true
                };

                const today = new Date();
                const deliveryEmailAddress = 'donorrelations@btrts.org.sg';

                const deliverySpecifications: DeliverySpecification = {
                    DeliveryModeId: 'Standard', // pickupFromStoreModeCode,
                    DeliveryPreferenceTypeValue: DeliveryPreferenceType.ShipToAddress,
                    ElectronicDeliveryEmailAddress: deliveryEmailAddress,
                    PickUpStoreId: '00000182',
                    DeliveryAddress: devliveryAddress,
                    RequestedDeliveryDate: today
                };

                await updateDeliverySpecificationAsync({ callerContext: props.context.actionContext }, cart.Id, deliverySpecifications)
                    .then(updatedCart => {
                        updatedCartVersion = updatedCart.Version;
                    })
                    .catch(error => {
                        props.context.actionContext.telemetry.exception(error);
                        throw new Error('[updateDeliverySpecificationAsync] Unable to update delivery mode in cart:');
                    });

                console.log('--updateCart--', updatedCartVersion);
                // if (!updateCart) {
                //     throw new Error('Unable to update delivery mode in cart by updateDeliverySpecificationAsync.');
                // }
                await cartState.refreshCart({});
            } catch (error) {
                //     const e = error as Error;
                //     console.log('---error--', e.message);
                //    // this.props.telemetry.warning(e.message);
                //     //this.props.telemetry.debug('Unable to update delivery specs in cart.');
                //     return AsyncResult.resolve();
            }
        } else {
            //  // this.props.telemetry.warning('Cart version not found for delivery specs update request.');
            //    console.log('--cart version does not found---');
            //   return AsyncResult.resolve();
        }
    }
}

export default Buybox;
